<template>
  <ValidationProvider
    ref="validationProvider"
    :rules="rules"
    v-slot="validationProps"
    :value="computedValue"
  >
    <section class="form-address" :class="{ expanded: !isCompact }">
      <p v-if="$slots.header" class="has-text-weight-medium m-b-20">
        <slot name="header" />
      </p>
      <div v-else-if="label.length" class="has-text-weight-semibold m-b-20">
        {{ label }}
      </div>
      <div class="columns is-mobile">
        <div class="column is-full">
          <FormAutoComplete
            ref="autocomplete"
            :value="displayAddress"
            :suggestions="suggestions"
            :suggestions-fn="getSuggestions"
            :display-property="GooglePlaceSuggestion.autoCompleteDisplayProperty"
            :loading="loadingSuggestions || loading"
            :placeholder="searchFieldLabel"
            :disabled="isReadOnlyInput('fullAddress')"
            @on-select="onSelect"
            @input="onInput"
            :data-e2e="GuidanceStepCypressAttribute.modal.inputs.STREET_ADDRESS"
          />
        </div>
      </div>
      <template v-if="!isCompact">
        <div v-if="searchBusinesses" class="columns is-mobile">
          <div class="column is-12">
            <FormInput
              v-model="computedValue.streetName"
              :placeholder="$t('core.street-address')"
              :read-only="isReadOnlyInput('streetName')"
              :rules="{ required: isRequired }"
              @input="onAddressChange"
            />
          </div>
        </div>
        <div class="columns is-multiline is-mobile">
          <div class="column" :class="columnClasses">
            <FormInput
              v-model="computedValue.aptNumber"
              :placeholder="$t('core.apartment')"
              :read-only="isReadOnlyInput('aptNumber')"
              @input="onAddressChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.APARTMENT"
            />
          </div>
          <div class="column" :class="[...columnClasses, { 'p-t-0': fullWidthFields }]">
            <FormInput
              v-model="computedValue.city"
              :placeholder="$t('address.city')"
              :rules="{ required: isRequired }"
              :read-only="isReadOnlyInput('city')"
              @input="onAddressChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.CITY"
            />
          </div>
        </div>
        <div class="columns is-mobile is-multiline">
          <div class="column" :class="columnClasses">
            <FormMultiSelect
              v-model="computedValue.countryId"
              :config="countrySelectConfig"
              :query-params="countryQueryParams"
              :rules="{ required: isRequired }"
              :disabled="isReadOnlyInput('countryId')"
              :allow-disabled-population="true"
              @input="onCountryChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.COUNTRY"
            />
          </div>
          <div class="column" :class="[...columnClasses, { 'p-t-0': fullWidthFields }]">
            <FormMultiSelect
              v-if="isActiveCountry"
              v-model="computedValue.jurisdictionId"
              :config="jurisdictionSelectConfig"
              :query-params="jurisdictionQueryParams"
              :rules="{ required: isRequired }"
              :disabled="isReadOnlyInput('jurisdictionId')"
              :allow-disabled-population="true"
              @input="onJurisdictionChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.JURISDICTION"
            />
            <FormInput
              v-else
              v-model="computedValue.administrativeDivision"
              :placeholder="$t('contact.state')"
              :read-only="isReadOnlyInput('jurisdictionId')"
              @input="onAddressChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.TEXT_JURISDICTION"
            />
          </div>
        </div>
        <div class="columns is-multiline is-mobile">
          <div v-if="showCounty" class="column" :class="columnClasses">
            <FormMultiSelect
              v-model="computedValue.countyId"
              :config="countySelectConfig"
              :query-params="countyQueryParams"
              :rules="{ required: isCountyRequired }"
              :disabled="isReadOnlyInput('countyId')"
              :allow-disabled-population="true"
              @input="onCountyChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.COUNTY"
            />
          </div>
          <div class="column" :class="columnClasses">
            <FormInput
              v-model="computedValue.postalCode"
              :rules="{ required: isRequired }"
              :placeholder="$t('core.zip-code')"
              :read-only="isReadOnlyInput('postalCode')"
              @input="onAddressChange"
              :data-e2e="GuidanceStepCypressAttribute.modal.inputs.POSTAL_CODE"
            />
          </div>
          <div class="column" :class="columnClasses" v-if="$slots.additionalInformation">
            <slot name="additionalInformation"></slot>
          </div>
        </div>
      </template>
    </section>
    <slot name="error" v-bind="validationProps">
      <template v-if="hasValidationErrors(validationProps)">
        <span class="validation-text">
          {{ getErrorFormattedText(validationProps) }}
        </span>
      </template>
    </slot>
  </ValidationProvider>
</template>

<script>
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import FormAutoComplete from '/@/modules/core/form/components/FormAutoComplete.vue';
import FormInput from '/@/modules/core/form/components/FormInput.vue';
import FormMultiSelect from '/@/modules/core/form/components/FormMultiSelect.vue';
import { GooglePlaceAutoCompleteService } from '/@/modules/core/geo/services/GooglePlaceAutoCompleteService';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import { GooglePlaceSuggestion } from '/@/modules/core/geo/models/GooglePlaceSuggestion';
import { GooglePlaceDetails } from '/@/modules/core/geo/models/GooglePlaceDetails';
import { Address } from '/@/modules/address/models/Address';
import { JurisdictionSelectConfig } from '/@/modules/predefined/models/JurisdictionSelectConfig';
import { CountrySelectConfig } from '/@/modules/predefined/models/CountrySelectConfig';
import { GuidanceStepCypressAttribute } from '/@/modules/guidancestep/constants/GuidanceStepCypressAttribute';
import Lang from '/@/modules/core/app/services/Lang';
import { FormReadOnlyMixin } from '/@/modules/core/form/mixins/FormReadOnlyMixin';
import { PredefinedCountry } from '/@/modules/predefined/enums/PredefinedCountry';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import { CountySelectConfig } from '/@/modules/predefined/models/CountySelectConfig';
import JurisdictionService from '/@/modules/predefined/services/JurisdictionService';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormAddress',
  emits: ['on-county-change', 'on-address-entered', 'input', 'on-business-entered'],
  slotsDefinition: {
    header: {},
    additionalInformation: {},
    error: { validationProps: Object }
  },
  mixins: [FormComputedValueMixin, FormReadOnlyMixin, FormValidationMixin, HasContainerMixin],
  components: {
    ValidationProvider,
    FormInput,
    FormAutoComplete,
    FormMultiSelect
  },
  props: {
    value: {
      type: Address
    },
    placeholder: { type: String },
    isCompact: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchBusinesses: {
      type: Boolean,
      default: false
    },
    businessName: {
      type: String,
      default: null
    },
    businessLabel: {
      type: String
    },
    fullWidthFields: {
      type: Boolean,
      default: false
    },
    searchGlobal: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    isCountyRequired: {
      type: Boolean,
      required: false
    },
    isRequired: {
      type: Boolean,
      default: true
    }
  },
  static: {
    GooglePlaceSuggestion,
    GuidanceStepCypressAttribute
  },
  local() {
    return {
      countySelectConfig: new CountySelectConfig(),
      countrySelectConfig: new CountrySelectConfig(),
      jurisdictionSelectConfig: new JurisdictionSelectConfig()
    };
  },
  data() {
    return {
      googlePlaceAutoCompleteService: this.$resolve(GooglePlaceAutoCompleteService),
      suggestions: [],
      loadingSuggestions: false,
      isStreetSearch: true,
      updatedBusinessName: this.businessName
    };
  },
  computed: {
    displayAddress() {
      const displayName = this.searchBusinesses
        ? this.updatedBusinessName
        : this.computedValue?.streetName;
      return this.isCompact ? this.computedValue?.fullAddress : displayName;
    },
    searchFieldLabel() {
      if (this.searchBusinesses) {
        return this.businessLabel ?? Lang.t('core.service-provider');
      }
      return this.placeholder ?? Lang.t(this.isCompact ? 'asset.address' : 'core.street-address');
    },
    countyQueryParams() {
      return this.showCounty ? { jurisdictionId: this.computedValue.jurisdictionId } : null;
    },
    jurisdictionQueryParams() {
      return this.isActiveCountry ? { countryId: this.computedValue.countryId } : null;
    },
    columnClasses() {
      return this.fullWidthFields ? ['is-full'] : ['is-half'];
    },
    suggestionTypes() {
      return this.searchBusinesses
        ? this.googlePlaceAutoCompleteService.getBusinessSuggestionsTypes()
        : null;
    },
    isActiveCountry() {
      return !!PredefinedCountry.getByKey(this.computedValue.countryId, 'id');
    },
    showCounty() {
      return JurisdictionService.isUSJurisdiction(this.computedValue.jurisdictionId);
    },
    countryQueryParams() {
      return { showOnlyActive: !this.searchGlobal };
    }
  },
  methods: {
    async getSuggestions(input) {
      if (!input?.length) {
        if (!this.isStreetSearch) {
          return;
        }
        this.suggestions = [];
        return;
      }

      this.isStreetSearch = true;
      this.loadingSuggestions = true;

      this.googlePlaceAutoCompleteService.input = input;

      await this.googlePlaceAutoCompleteService.getSuggestions(
        input,
        this.onSuggestions,
        this.suggestionTypes,
        this.searchGlobal
      );
      this.loadingSuggestions = false;
    },
    onSuggestions(suggestions) {
      this.suggestions = GooglePlaceSuggestion.mapModels(suggestions);
    },
    onInput(input) {
      if (this.isCompact) {
        return;
      }

      if (this.searchBusinesses) {
        this.onBusinessSelected(input);
      } else {
        this.computedValue.streetName = input;
      }
    },
    async onSelect(selection) {
      if (!selection) {
        return;
      }
      this.onAddressChange();

      if (!selection?.placeId) {
        this.computedValue.fullAddress = selection.description;
        return;
      }

      await this.googlePlaceAutoCompleteService.getPlaceDetails(
        selection.placeId,
        async (details) => {
          const placeDetails = GooglePlaceDetails.mapModel({
            ...details,
            description: selection.description
          });
          this.isStreetSearch = !!placeDetails.streetName;
          await this.computedValue.setFromGooglePlaceDetails(placeDetails);
          this.$refs.autocomplete.hideDropdown();

          if (this.searchBusinesses) {
            this.updatedBusinessName = this.computedValue.businessName;
            this.onBusinessSelected(this.updatedBusinessName);
          }
          if (this.computedValue.countyId) {
            this.onCountyChange();
          }
        }
      );
    },
    onCountryChange() {
      this.computedValue.jurisdictionId = null;
      this.computedValue.countyId = null;
      this.onCountyChange();
    },
    onJurisdictionChange() {
      this.computedValue.countyId = null;
      this.onCountyChange();
    },
    onCountyChange() {
      this.$emit('on-county-change', this.computedValue.countyId);
      this.onAddressChange();
    },
    onAddressChange() {
      this.$emit('on-address-entered', this.computedValue.fullAddress);
      this.$emit('input', this.computedValue);
    },
    onBusinessSelected(value) {
      this.$emit('on-business-entered', value ?? this.displayAddress);
    }
  },
  created() {
    this.computedValue = this.value ?? Address.mapModel();
  }
};
</script>

<style scoped>
:deep(.autocomplete .input) {
  height: var(--ce-40px);
}
.columns,
.column {
  margin-bottom: 0;
}
.column {
  padding-bottom: 0;
}
</style>
